import { useNavigate, useSearch } from "@tanstack/react-router";
import * as React from "react";
import styled from "styled-components";
import { tabToRoute, useCurrentTab } from "@/hooks/useCurrentTab";
import { useTranslations } from "@/queries/useTranslations";
import { getAllModules, getModulesForContent } from "@/translationFiltering";
import type { IContentType } from "@/types";
import { normalizeModsParam } from "@/urlState";

const Wrapper = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    label {
        margin-top: 0.5rem;
    }
`;

interface IProps {
    contentType: IContentType;
    version: string;
}

function ModuleSelector({ contentType, version }: IProps) {
    const { data: translations } = useTranslations(version);
    const navigate = useNavigate({ from: "/$version" });
    const mods = useSearch({ from: "/$version", select: (state) => state.mods });
    const tab = useCurrentTab();

    const modules = React.useMemo(() => {
        return translations ? getModulesForContent(translations, contentType) : [];
    }, [translations, contentType]);
    const allModules = React.useMemo(() => {
        return translations ? getAllModules(translations) : [];
    }, [translations]);
    const enabled = new Set(mods ?? allModules);

    const toggle = (event: React.FormEvent<HTMLInputElement>) => {
        const mod = event.currentTarget.name;
        const checked = event.currentTarget.checked;

        const next = new Set(enabled);
        if (checked) {
            next.add(mod);
        } else {
            next.delete(mod);
        }

        const nextMods = normalizeModsParam(Array.from(next), allModules);
        const to = tabToRoute(tab);
        navigate({
            to,
            params: { version },
            search: (prev) => ({ ...prev, mods: nextMods, page: undefined }),
        });
    };

    return (
        <fieldset>
            <legend>Select modules to show</legend>
            <Wrapper>
                {modules.map((mod) => (
                    <label key={mod}>
                        <input name={mod} type="checkbox" checked={enabled.has(mod)} onChange={toggle} /> {mod}
                    </label>
                ))}
            </Wrapper>
        </fieldset>
    );
}

export default React.memo(ModuleSelector);
